import React from 'react'
import { Card, Badge } from 'react-bootstrap'
import { css } from 'emotion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTruck, faPrint } from '@fortawesome/free-solid-svg-icons'
import Select from 'react-select'

const PrintVehicleSelector = ({ vehicles, selectedVehicles, onVehicleSelection, loading }) => {
  
  if (loading) {
    return (
      <Card className={css(styles.selectorCard)}>
        <Card.Body>
          <div className={css(styles.loadingContainer)}>
            <FontAwesomeIcon icon={faTruck} spin className={css(styles.spinner)} />
            Chargement des véhicules...
          </div>
        </Card.Body>
      </Card>
    )
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <Card className={css(styles.selectorCard)}>
        <Card.Body>
          <p className={css(styles.noData)}>Aucune tournée avec des étapes ce jour.</p>
        </Card.Body>
      </Card>
    )
  }

  // Préparer les options pour react-select
  const vehicleOptions = vehicles
  .map(vehicle => {
    const itineraryCount = vehicle.itinerary ? vehicle.itinerary.length : 0
    return {
      value: vehicle.id,
      label: `${vehicle.name} (${itineraryCount} étape${itineraryCount > 1 ? 's' : ''})`,
      vehicle: vehicle
    }
  })
  .sort((a, b) => a.vehicle.name.localeCompare(b.vehicle.name, 'fr'))

  // Préparer les valeurs sélectionnées
  const selectedOptions = selectedVehicles.map(vehicle => {
    const itineraryCount = vehicle.itinerary ? vehicle.itinerary.length : 0
    return {
      value: vehicle.id,
      label: `${vehicle.name} (${itineraryCount} étape${itineraryCount > 1 ? 's' : ''})`,
      vehicle: vehicle
    }
  })

  const handleVehicleChange = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : []
    onVehicleSelection(selectedIds)
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <Card className={css(styles.selectorCard, styles.noPrint)}>
      <Card.Header className={css(styles.cardHeader)}>
        Sélection des véhicules
        {selectedVehicles.length > 0 && (
          <Badge variant="primary" className={css(styles.countBadge)}>
            {selectedVehicles.length} véhicule{selectedVehicles.length > 1 ? 's' : ''} sélectionné{selectedVehicles.length > 1 ? 's' : ''}
          </Badge>
        )}
      </Card.Header>
      <Card.Body>
        <div className={css(styles.selectorContainer)}>
          <Select
            isMulti
            options={vehicleOptions}
            value={selectedOptions}
            onChange={handleVehicleChange}
            placeholder="Choisir des véhicules..."
            noOptionsMessage={() => "Aucun véhicule disponible"}
            className={css(styles.reactSelect)}
            classNamePrefix="react-select"
            styles={customSelectStyles}
          />
          
          {selectedVehicles.length > 0 && (
            <div className={css(styles.printButtonContainer)}>
              <button 
                onClick={handlePrint}
                className={css(styles.printButton)}
              >
                <FontAwesomeIcon icon={faPrint} className={css(styles.printIcon)} />
                Imprimer les itinéraires
              </button>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}

// Styles personnalisés pour react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '45px',
    border: state.isFocused ? '2px solid #007bff' : '2px solid #e9ecef',
    borderRadius: '5px',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
    '&:hover': {
      borderColor: '#007bff'
    }
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#e3f2fd',
    borderRadius: '4px'
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#007bff',
    fontWeight: '500'
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#007bff',
    '&:hover': {
      backgroundColor: '#007bff',
      color: 'white'
    }
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#6c757d'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? '#007bff' 
      : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
    }
  })
}

const styles = {
  selectorCard: {
    marginBottom: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  noPrint: {
    '@media print': {
      display: 'none !important'
    }
  },
  cardHeader: {
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
    fontWeight: 'bold',
    color: '#495057',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  icon: {
    marginRight: '8px',
    color: '#007bff'
  },
  countBadge: {
    fontSize: '0.8rem'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    color: '#6c757d'
  },
  spinner: {
    marginRight: '10px'
  },
  noData: {
    textAlign: 'center',
    padding: '20px',
    color: '#6c757d'
  },
  selectorContainer: {
    padding: '0'
  },
  reactSelect: {
    fontSize: '1rem',
    marginBottom: '15px'
  },
  printButtonContainer: {
    textAlign: 'center',
    paddingTop: '15px',
    borderTop: '1px solid #e9ecef'
  },
  printButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#218838'
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 0.2rem rgba(40, 167, 69, 0.25)'
    }
  },
  printIcon: {
    marginRight: '8px'
  }
}

export default PrintVehicleSelector
